import { pixi, random } from './alias'
import { Layout } from './layout'
import { GamePlay, TrackOfThoughtConfig } from './main'
import { Score } from './score'
import { Sketcher } from './sketch'
import { SmartSwitch } from './tools/smartSwitchGrid'
import { Direction, Position, Tile } from './type'
import { Departure, Destination, Switch } from './type/tileType'
import { directionToDelta, isStraight, oppositeOf } from './util/direction'
import { isRail, isStation } from './util/tile'

export interface TrainManagerParam {
  colorList: number[]
  config: TrackOfThoughtConfig
  container: pixi.Container
  departure: Departure
  destinationArray: Destination[]
  graphicalGrid: (pixi.Container | null)[][]
  grid: (Tile | null)[][]
  layout: Layout
  randomEngine: random.Engine
  score: Score
  sketch: Sketcher
  smartSwitchGrid: (SmartSwitch | null)[][]
  toggleSwitch: (g: pixi.Container, rail: Switch) => void
  updateSwitchColor: (x: number, y: number) => void
  getTrainInterval: () => number
}

export function createTrainManager(param: TrainManagerParam) {
  const {
    colorList,
    config,
    container,
    departure,
    destinationArray,
    grid,
    layout,
    randomEngine,
    score,
    sketch,
    updateSwitchColor,
    smartSwitchGrid,
    graphicalGrid,
    toggleSwitch,
    getTrainInterval,
  } = param

  let trainArray: Train[] = []
  let trainTime = 0
  let spawningEnabled = true

  const addTrain = () => {
    // Don't spawn new trains if spawning is disabled (timer ended)
    if (!spawningEnabled) return
    
    // Spawn a new train when enough time has passed
    const trainPeriod = getTrainInterval()
    while (trainTime >= trainPeriod) {
      trainTime -= trainPeriod

      let train = createTrain({
        autoPlayOnEntry: config.autoPlayOnEntry,
        autoPlayOnExit: config.autoPlayOnExit,
        colorIndex: random.pick(randomEngine, destinationArray).colorIndex,
        colorList,
        gamePlay: config.gamePlay,
        graphicalGrid,
        grid,
        layout,
        position: departure,
        score,
        sketch,
        smartSwitchGrid,
        toggleSwitch,
        updateSwitchColor,
      })
      container.addChild(train.g)
      trainArray.push(train)

      train.progress += trainTime
    }
  }

  return {
    update(timeStep: number) {
      trainTime += timeStep / 60
      addTrain()

      // remove the trains which have reached a station
      trainArray = trainArray.filter((train) => {
        if (!train.running) {
          if (config.gamePlay === 'station') {
            score.scoreEvent(train.colorMatchesStation())
          }

          container.removeChild(train.g)
          train.g.destroy()
          return false
        }
        return true
      })

      // update the trains
      trainArray.forEach((train) => {
        train.update(timeStep)
      })
    },
    
    // Stop spawning new trains (called when timer ends)
    stopSpawning() {
      spawningEnabled = false
    },
    
    // Check if all trains have finished (reached stations)
    allTrainsFinished() {
      return !spawningEnabled && trainArray.length === 0
    },
    
    // Get count of remaining trains
    getRemainingTrainCount() {
      return trainArray.length
    },
  }
}

export interface TrainParam {
  autoPlayOnEntry: boolean
  autoPlayOnExit: boolean
  colorIndex: number
  colorList: number[]
  gamePlay: GamePlay
  graphicalGrid: (pixi.Container | null)[][]
  grid: (Tile | null)[][]
  layout: Layout
  position: Position
  score: Score
  sketch: Sketcher
  smartSwitchGrid: (SmartSwitch | null)[][]
  toggleSwitch: (g: pixi.Container, rail: Switch) => void
  updateSwitchColor: (x: number, y: number) => void
}

export function createTrain(param: TrainParam) {
  let position = { ...param.position }
  let {
    autoPlayOnEntry,
    autoPlayOnExit,
    colorIndex,
    colorList,
    gamePlay,
    graphicalGrid,
    grid,
    layout,
    score,
    sketch,
    smartSwitchGrid,
    toggleSwitch,
    updateSwitchColor,
  } = param

  let g = sketch.train(colorIndex, colorList)

  const addTrainToSwitch = (tile: Switch, x: number, y: number) => {
    tile.trainArray.push(me)
    updateSwitchColor(x, y)
  }
  const removeTrainFromSwitch = (tile: Switch, x: number, y: number) => {
    tile.trainArray.splice(tile.trainArray.indexOf(me), 1)
    updateSwitchColor(x, y)
  }
  // Smooth easing function for more natural movement
  const easeInOutQuad = (t: number): number => {
    return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2
  }

  const update = (timeStep: number) => {
    let { x, y } = position
    let tile = grid[y][x]
    let tileIsSwitch = tile?.type === 'switch'
    const next = () => {
      if (!tile) {
        throw new Error(`encountered a null entry in the grid, at ${y}:${x}`)
      }
      if (tile.type === 'destination') {
        throw new Error('never, destination')
      }

      let { dx, dy } = directionToDelta(tile.exit)
      position.x += dx
      position.y += dy
      ;({ x, y } = position)
      tile = grid[y][x]
      tileIsSwitch = tile?.type === 'switch'
    }
    // Smoother movement with smaller time step divisor
    me.progress += timeStep / 90
    if (me.progress > 1) {
      // Only auto-switch in autoPlay modes (not in normal station mode)
      if (autoPlayOnExit) {
        setSwitch()
      }
      if (tileIsSwitch) {
        removeTrainFromSwitch(tile as Switch, x, y)
      }
      while (me.progress > 1) {
        next()
        me.progress -= 1
      }
      if (tileIsSwitch) {
        addTrainToSwitch(tile as Switch, x, y)
      }
      if (autoPlayOnEntry) {
        // we've just arrived on the tile, so we aren't in a hurry to set it to
        // the right direction, so use the weak mode so that if there is already
        // a train on the switch, the switch won't be toggled.
        let weak = true
        setSwitch(weak)
      }
    }
    if (tileIsSwitch) {
      updateSwitchColor(x, y)
    }
    updatePosition()
    if (grid[position.y][position.x]?.type === 'destination' && me.progress >= 0.5) {
      me.running = false
    }
  }
  // in autoplay mode, toggle the switch if necessary for the train to reach its
  // destination
  const setSwitch = (weak = false) => {
    let { x, y } = position
    let tile = grid[y][x]
    if (tile?.type === 'switch') {
      if (weak && tile.trainArray.length > 1) {
        return
      }
      let smartSwitch = smartSwitchGrid[y][x]!
      let colorArray =
        tile.state === 'initial' ? smartSwitch.colorIndexArray : smartSwitch.colorIndexOtherArray
      let changeSwitch = colorArray.includes(me.colorIndex)
      if (changeSwitch) {
        let g = graphicalGrid[y][x]!
        toggleSwitch(g, tile)
      }
      return changeSwitch
    }
  }

  // Helper to get rotation angle for a direction
  const directionToAngle = (dir: Direction): number => {
    switch (dir) {
      case 'right': return 0
      case 'bottom': return Math.PI / 2
      case 'left': return Math.PI
      case 'top': return -Math.PI / 2
    }
  }

  const updatePosition = () => {
    let tile = grid[position.y][position.x]!

    let entrance: Direction
    let exit: Direction
    if (isRail(tile)) {
      ;({ entrance, exit } = tile)
    } else if (tile.type === 'departure') {
      ;({ exit } = tile)
      entrance = oppositeOf(exit)
    } else if (tile.type === 'destination') {
      ;({ entrance } = tile)
      exit = oppositeOf(entrance)
    } else {
      throw new Error('never')
    }

    // Apply smooth easing to the progress for visual smoothness
    const smoothProgress = easeInOutQuad(me.progress)

    // Calculate base position (center of tile)
    let baseX = layout.squareWidth * position.x + layout.squareWidth / 2
    let baseY = layout.squareWidth * position.y + layout.squareWidth / 2

    // Calculate offset based on movement
    let offsetX = 0
    let offsetY = 0

    // Get start and end angles for rotation interpolation
    // The train enters from 'entrance' direction, so it's facing opposite of entrance
    const startAngle = directionToAngle(oppositeOf(entrance))
    const endAngle = directionToAngle(exit)

    if (isStraight(entrance, exit)) {
      if (exit === 'right') {
        offsetX = (smoothProgress - 0.5) * layout.squareWidth
      } else if (exit === 'left') {
        offsetX = (0.5 - smoothProgress) * layout.squareWidth
      } else if (exit === 'bottom') {
        offsetY = (smoothProgress - 0.5) * layout.squareWidth
      } else if (exit === 'top') {
        offsetY = (0.5 - smoothProgress) * layout.squareWidth
      }
      // For straight paths, rotation stays constant
      g.rotation = endAngle
    } else {
      // Smoother curve movement using eased progress
      let from = ((Math.sin((smoothProgress * Math.PI) / 2) - 1) * layout.squareWidth) / 2
      let to = ((Math.cos((smoothProgress * Math.PI) / 2) - 1) * layout.squareWidth) / 2
      if (entrance === 'top') offsetY += from
      if (entrance === 'left') offsetX += from
      if (entrance === 'right') offsetX -= from
      if (entrance === 'bottom') offsetY -= from

      if (exit === 'top') offsetY += to
      if (exit === 'left') offsetX += to
      if (exit === 'right') offsetX -= to
      if (exit === 'bottom') offsetY -= to

      // For curves, smoothly interpolate the rotation
      // Need to handle angle wrapping (e.g., from PI to -PI/2)
      let angleDiff = endAngle - startAngle
      
      // Normalize angle difference to [-PI, PI] for shortest path
      while (angleDiff > Math.PI) angleDiff -= 2 * Math.PI
      while (angleDiff < -Math.PI) angleDiff += 2 * Math.PI
      
      g.rotation = startAngle + angleDiff * smoothProgress
    }

    // Set pivot to center for proper rotation
    g.pivot.set(layout.squareWidth / 2, layout.squareWidth / 2)
    
    // Apply final position
    g.x = baseX + offsetX
    g.y = baseY + offsetY
  }

  const colorMatchesStation = () => {
    let s = grid[position.y][position.x]
    if (!s || !isStation(s)) {
      throw new Error('train is not at a station')
    }
    return me.colorIndex === (s as any).colorIndex
  }

  let me = {
    g,
    running: true,
    progress: 0.5,
    colorIndex,
    update,
    colorMatchesStation,
  }

  update(0)

  return me
}

export type Train = ReturnType<typeof createTrain>
